import React, {
    FC,
    useState,
    useEffect,
    MouseEvent,
    ChangeEvent,
    KeyboardEvent,
    useRef,
    useMemo,
    useContext
} from 'react';
import { useApolloClient } from '@apollo/client';
import HighlightedText from './HighlightedText/HighlightedText';
import { CloseMedium, PinSmall } from '@zoopla-eng/zds-components/icons';
import {
    ADDRESS_POSTCODE_SUGGESTION_QUERY,
    GEO_LOCATIONS_QUERY
} from '@zoopla/listing-search-queries';
import {
    buttonTextStyle,
    iconButtonStyle,
    iconWrapperStyle,
    inputStyle,
    listItemStyle,
    listStyle,
    styledWrapperStyle,
    suggestionButtonStyle,
    valueStyle,
    visuallysHiddenItem
} from './InputLocation.styles.css';
import { SearchBarContext } from '../SearchBar';
import { InputField } from '@zoopla-eng/zds-components/input-field';
import { IconButton } from '@zoopla-eng/zds-components/button';
import debounce from 'lodash.debounce';
import { coerceQueryParameter } from '../../utils/coerceQueryParameter';
import { useGetSearchLocationFromPath } from '@zoopla/listing-search/get-search-location-from-path';

type GeoSuggestion = {
    geoIdentifier: string;
    geoLabel: string;
    geoSubLabel?: string;
};

type SuggestedAddress = {
    address: string;
    urlPath: string;
};

type Option = {
    id: string;
    text: string;
};

export enum QueryType {
    GeoLocations = 'GEO_LOCATIONS',
    AddressPostcodes = 'ADDRESS_POSTCODE'
}

export type Item = {
    id: string;
    text: string;
    subValue?: string | null;
};

export type InputAutoSuggestWithLabelProps = {
    label?: string;
    queryType?: QueryType;
} & React.InputHTMLAttributes<HTMLInputElement>;

const validInputRegex = /([A-Za-zÀ-ÖØ-öø-ÿ])/;

const CLEAR_ICON_VALUE = 'Clear';

const dict = {
    ArrowUp: 38,
    ArrowDown: 40,
    Escape: 27,
    Enter: 13
} as const;

function checkKeyEvent(evt: KeyboardEvent, code: keyof typeof dict): boolean {
    if (evt.code) {
        return evt.code === code;
    }
    if (evt.keyCode !== 0) {
        return dict[code] ? dict[code] === evt.keyCode : false;
    }
    return false;
}

export const InputLocation: FC<
    React.PropsWithChildren<InputAutoSuggestWithLabelProps>
> = ({ label = 'Search area', queryType = QueryType.GeoLocations }) => {
    const apolloClient = useApolloClient();
    const searchLocationFromPath = useGetSearchLocationFromPath();
    const { handleSearchProperties, hasError, formValue, updateFormValue } =
        useContext(SearchBarContext);
    const [suggestions, setSuggestions] = useState<Item[]>([]);
    const [suggestionIdx, setSuggestionIdx] = useState(-1);

    const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const rootNode = useRef<HTMLDivElement>(null);
    const inputNode = useRef<HTMLInputElement>(null);
    const abortSignalRef = useRef<AbortSignal | null>(null);

    useEffect(() => {
        setSearchText(
            coerceQueryParameter(formValue.q || searchLocationFromPath.label)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        abortSignalRef.current = controller.signal;
        return () => {
            controller.abort();
        };
    }, []);

    const fetchGeoItems = useMemo(
        () => async (value: string | null) => {
            if (!value) return [];
            try {
                const { data } = await apolloClient.query({
                    query: GEO_LOCATIONS_QUERY,
                    variables: { locationPrefix: value },
                    context: {
                        fetchOptions: {
                            signal: abortSignalRef.current
                        }
                    }
                });

                return data?.geoSuggestion?.map(
                    ({ geoLabel, geoIdentifier }: GeoSuggestion) => ({
                        id: geoIdentifier,
                        text: geoLabel
                    })
                );
            } catch (e) {
                // use previous state on error
                return (pendingState: Option[]) => pendingState;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const fetchAddressItems = useMemo(
        () => async (value: string | null) => {
            if (!value) return [];
            try {
                const { data } = await apolloClient.query({
                    query: ADDRESS_POSTCODE_SUGGESTION_QUERY,
                    variables: { addressPartial: value.toLowerCase() },
                    context: {
                        fetchOptions: {
                            signal: abortSignalRef.current
                        }
                    }
                });

                const addressSuggestions =
                    data.getSuggestedAddresses?.suggestions;

                return addressSuggestions
                    ? addressSuggestions?.map(
                          ({ address, urlPath }: SuggestedAddress) => ({
                              id: urlPath || '',
                              text: address
                          })
                      )
                    : [];
            } catch (e) {
                // use previous state on error
                return (pendingState: Option[]) => pendingState;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const setSearchText = (value: string) => {
        if (!inputNode?.current) {
            return;
        }

        inputNode.current.value = value;
    };

    const getData =
        queryType === QueryType.AddressPostcodes
            ? fetchAddressItems
            : fetchGeoItems;

    const inputOnKeyDown = (evt: KeyboardEvent) => {
        evt.preventDefault();

        if (evt.key === 'Enter') {
            handleSearchProperties?.();
        }
    };

    useEffect(() => {
        let unmounted = false;
        function onClick(evt: globalThis.MouseEvent) {
            if (
                rootNode.current &&
                evt.target instanceof Node &&
                rootNode.current.contains(evt.target)
            ) {
                return;
            }

            if (unmounted) return;

            setSuggestions([]);
            setSuggestionIdx(-1);
        }

        if (typeof window !== 'undefined') {
            document.addEventListener('mousedown', onClick);
        }

        return () => {
            unmounted = true;
            if (typeof window !== 'undefined') {
                document.removeEventListener('mousedown', onClick);
            }
        };
    }, []);

    const suggestionSelectedAction = ({
        id,
        value
    }: {
        id: string;
        value: string;
    }) => {
        updateFormValue?.((oldFormValue) => ({
            ...oldFormValue,
            q: value,
            geo_autocomplete_identifier: id
        }));
    };

    /* Deboounced to reduce re-renders as use types */
    const handleInputTextChange = useMemo(
        () =>
            debounce((value: string) => {
                updateFormValue?.((oldFormValue) => ({
                    ...oldFormValue,
                    q: value,
                    geo_autocomplete_identifier: ''
                }));
            }, 75),
        [updateFormValue]
    );

    const onTextChange = async (evt: ChangeEvent<HTMLInputElement>) => {
        const {
            target: { value }
        } = evt;

        handleInputTextChange(value);

        if (!validInputRegex.test(value)) {
            return setSuggestions([]);
        }

        const data = await getData(value);
        if (abortSignalRef.current?.aborted) return;
        setSuggestions(data || []);
    };

    const onClearButtonClick = (
        event:
            | React.MouseEvent<HTMLButtonElement>
            | KeyboardEvent<HTMLButtonElement>
            | undefined
    ) => {
        if (event) {
            event.preventDefault();
        }
        setSearchText('');
        setSuggestions([]);
        setSuggestionIdx(-1);
        updateFormValue?.((oldFormValue) => ({
            ...oldFormValue,
            q: '',
            geo_autocomplete_identifier: ''
        }));
        inputNode.current?.focus();
    };

    const onSuggestionClick = (evt: MouseEvent<HTMLButtonElement>) => {
        const {
            currentTarget: {
                value,
                dataset: { id = '' }
            }
        } = evt;

        setSuggestions([]);
        setSearchText(value);

        if (suggestionSelectedAction) {
            suggestionSelectedAction({ id, value });
        }
    };

    const onKeyDown = (evt: KeyboardEvent) => {
        const { length } = suggestions;
        let idx = -1;

        if (suggestionIdx === -1 && checkKeyEvent(evt, 'Enter')) {
            if (inputOnKeyDown) {
                inputOnKeyDown(evt);
                setSuggestions([]);
                setSuggestionIdx(-1);

                if (inputNode?.current?.value) {
                    handleInputTextChange(inputNode?.current?.value);
                }
            }
            return;
        }

        if (checkKeyEvent(evt, 'ArrowUp') || checkKeyEvent(evt, 'ArrowDown')) {
            evt.preventDefault();
        }

        if (checkKeyEvent(evt, 'ArrowUp') && suggestionIdx > 0) {
            idx = suggestionIdx - 1;
        } else if (checkKeyEvent(evt, 'ArrowUp') && suggestionIdx === 0) {
            idx = length - 1;
        } else if (
            checkKeyEvent(evt, 'ArrowDown') &&
            suggestionIdx < length - 1
        ) {
            idx = suggestionIdx + 1;
        } else if (
            checkKeyEvent(evt, 'ArrowDown') &&
            suggestionIdx === length - 1
        ) {
            idx = 0;
        } else if (checkKeyEvent(evt, 'Escape') && suggestions.length !== 0) {
            setSearchText('');
            setSuggestions([]);
            setSuggestionIdx(-1);
        } else if (checkKeyEvent(evt, 'Enter')) {
            if (suggestions.length) {
                evt.stopPropagation();
                setSuggestions([]);
                const currentEl =
                    itemRefs.current[suggestionIdx === -1 ? 0 : suggestionIdx];
                const currentValue = currentEl?.value || '';
                const currentId = currentEl?.dataset['id'] || '';

                setSearchText(currentValue);

                if (suggestionSelectedAction) {
                    suggestionSelectedAction({
                        id: currentId,
                        value: currentValue
                    });
                }
            } else {
                if (suggestionSelectedAction) {
                    suggestionSelectedAction({
                        id: '',
                        value: inputNode?.current?.value || ''
                    });
                }
            }
        }

        setSuggestionIdx(idx);
    };

    const InputEmpty = inputNode?.current?.value === '';

    return (
        <div
            className={styledWrapperStyle}
            onKeyDown={onKeyDown}
            ref={rootNode}
        >
            <div
                className={styledWrapperStyle}
                role="combobox"
                aria-controls="input-location-options"
                aria-haspopup="listbox"
                aria-owns="input-location-options"
                aria-expanded={suggestions.length > 0}
            >
                <InputField
                    name="autosuggest-input"
                    label={label}
                    className={inputStyle({
                        showError: hasError && InputEmpty
                    })}
                    onChange={onTextChange}
                    aria-label={label}
                    ref={inputNode}
                    placeholder="eg. Oxford or NW3"
                    errorText={
                        hasError && InputEmpty
                            ? 'Please enter a search area'
                            : ''
                    }
                    aria-autocomplete="list"
                    spellCheck="false"
                    {...(suggestionIdx > -1
                        ? {
                              'aria-activedescendant': `suggestion-id-${suggestionIdx}`
                          }
                        : {})}
                    autoComplete="off"
                />
                {InputEmpty === false ? (
                    <div className={iconButtonStyle()}>
                        <IconButton
                            label="clear input"
                            className={iconButtonStyle()}
                            category="tertiary"
                            value={CLEAR_ICON_VALUE}
                            onClick={onClearButtonClick}
                            onKeyDown={(
                                event: React.KeyboardEvent<HTMLButtonElement>
                            ) => {
                                if (
                                    event.key === 'Enter' ||
                                    event.keyCode === 13
                                ) {
                                    event.stopPropagation();
                                    onClearButtonClick(event);
                                }
                            }}
                            data-testid="autosuggest-clear"
                            aria-label={CLEAR_ICON_VALUE}
                        >
                            {CloseMedium}
                        </IconButton>
                    </div>
                ) : null}
            </div>
            <div id="instructions" className={visuallysHiddenItem}>
                Use up and down arrow keys to navigate. Press Enter key to
                select.
            </div>
            <div
                id="results-count"
                role="status"
                aria-atomic="true"
                className={visuallysHiddenItem}
            >
                {`${suggestions.length} results found ${
                    suggestions.length > 0
                        ? 'Use up and down arrow keys to navigate. Press Enter key to select.'
                        : ''
                }`}
            </div>
            <div
                id="selected-item"
                role="status"
                aria-atomic="true"
                className={visuallysHiddenItem}
            >{`${
                suggestionIdx > -1
                    ? `${suggestions[suggestionIdx].text}, selected`
                    : ''
            }`}</div>
            <ul
                className={listStyle({
                    visuallyHidden: suggestions.length > 0
                })}
                role="listbox"
                id="input-location-options"
                aria-describedby="instructions results-count selected-item"
            >
                {suggestions.map((item, idx) => (
                    <li
                        className={listItemStyle}
                        key={item.id}
                        role="option"
                        aria-selected={idx === suggestionIdx}
                        id={`suggestion-id-${idx}`}
                        aria-labelledby="selected-item"
                        aria-hidden="true"
                    >
                        <button
                            aria-hidden="true"
                            className={suggestionButtonStyle({
                                isFocused: idx === suggestionIdx
                            })}
                            data-testid={item.text}
                            type="button"
                            value={item.text}
                            data-id={item.id}
                            onClick={onSuggestionClick}
                            ref={(ref) => {
                                itemRefs.current[idx] = ref;
                            }}
                        >
                            <div className={iconWrapperStyle}>
                                <PinSmall color="currentColor" />
                            </div>
                            <span className={buttonTextStyle}>
                                {item.subValue ? (
                                    <span
                                        className={valueStyle}
                                        data-testid="styled-value"
                                    >
                                        <HighlightedText
                                            text={item.text}
                                            highlight={
                                                inputNode?.current?.value || ''
                                            }
                                        />
                                    </span>
                                ) : (
                                    <HighlightedText
                                        text={item.text}
                                        highlight={
                                            inputNode?.current?.value || ''
                                        }
                                    />
                                )}
                                {item.subValue}
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
};
